import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useLocation, useNavigate } from 'react-router';

import { ENDPOINTS } from 'src/constants/api';
import { QueryKeys } from 'src/constants/queryKeys';
import { UserRoles } from 'src/constants/user';
import { getApiClient } from 'src/hooks/apiClient';

import { getConfig } from '@/config';
import { clearAuthState } from '@/features/auth/clearAuthState';
import { authTokenStorage } from '@/features/auth/external/authTokenStorage';
import { useAuthStore } from '@/store/authStore';
import { Nullable } from '@/types';
import { AnalysisConfig, UIVersion } from '@/types/config';
import {
  anyFeatureRequiresConsent,
  getGuestAppFlow,
  getUiVersion,
  GuestAppFlow,
} from '@/utils/consent';
import { clearAllSessionCaches } from '@/utils/teamsSessionCache';

export type UserProfileResponse = {
  created_at: string;
  user_id: string;
  is_admin: boolean;
  is_platform_admin: boolean;
  is_recording_enabled: boolean;
  in_app_alerts_enabled?: boolean;
  quota?: {
    expires_at_epoch: number;
    num_concurrent_bots: number;
    quota_id: string;
  };
  entitlement: Record<string, boolean | string>;
  uuid: string;
  preferences?: {
    auto_join_enabled: boolean;
  };
  roles?: UserRoles[];
  audio_liveness_config: Nullable<AnalysisConfig>;
  video_liveness_config: Nullable<AnalysisConfig>;
  voice_match_config: Nullable<AnalysisConfig>;
  location_config: Nullable<AnalysisConfig>;

  // derived fields
  consent_enabled: boolean;
  guestAppFlow: Nullable<GuestAppFlow>;
  uiVersion?: UIVersion;
};

export async function fetcher(token: string) {
  const apiClient = await getApiClient({ token });

  try {
    const { data } = await apiClient.get<
      UserProfileResponse,
      AxiosResponse<UserProfileResponse>
    >(ENDPOINTS.user);

    return {
      ...data,
    };
  } catch (err: unknown) {
    if ((err as AxiosError).status === 404) {
      await apiClient.post<
        UserProfileResponse,
        AxiosResponse<UserProfileResponse>
      >(ENDPOINTS.user);

      const { data } = await apiClient.get<
        UserProfileResponse,
        AxiosResponse<UserProfileResponse>
      >(ENDPOINTS.user);

      return {
        ...data,
        roles: data.is_admin
          ? [UserRoles.ADMIN, ...(data.roles ?? [])]
          : [UserRoles.USER],
      };
    }
    throw err; // Re-throw other errors
  }
}

interface UseGetUserOptions {
  redirectPath?: string;
}

export function useGetUser(options: UseGetUserOptions = {}) {
  const { redirectPath } = options;
  const { getAccessTokenSilently, logout } = useAuth0();
  const isExternalAuth = useAuthStore((state) => state.isExternalAuth);
  const location = useLocation();
  const navigate = useNavigate();

  return useQuery({
    queryKey: [QueryKeys.USER, redirectPath],
    queryFn: async () => {
      // trying to get token from local storage first,
      // if we are in companion app context - we may have prev stored token
      // so we can use it even if we dont have isExternalAuth set yet
      const token = authTokenStorage.get();

      if (isExternalAuth || token) {
        if (!token) {
          if (redirectPath) {
            console.warn('No token found, redirecting to:', redirectPath);
            navigate(redirectPath, { replace: true });
          }
          return null;
        }

        try {
          return await fetcher(token);
        } catch (e) {
          console.error('Failed to fetch user:', e);
          if ((e as AxiosError).status === 401) {
            clearAllSessionCaches();
          }
          if (redirectPath) {
            console.warn('Failed to fetch user, redirecting to:', redirectPath);
            navigate(redirectPath, { replace: true });
          }
          return null;
        }
      }

      try {
        const accessToken = await getAccessTokenSilently();

        return await fetcher(accessToken);
      } catch (err: unknown) {
        if (
          !isExternalAuth &&
          location.pathname.includes('/web') &&
          !location.pathname.includes('/webex')
        ) {
          clearAuthState();

          const { auth0ClientId, auth0RedirectUrl } = getConfig().AUTH;

          await logout({
            clientId: auth0ClientId,
            logoutParams: {
              returnTo: auth0RedirectUrl,
            },
          });
        }

        return null;
      }
    },
    refetchOnWindowFocus: isExternalAuth,
    refetchOnMount: isExternalAuth,
    staleTime: isExternalAuth ? 0 : 5 * 60 * 1000, // 5 minutes,
    select: (data) => {
      if (!data) {
        return null;
      }

      return {
        ...data,
        consent_enabled: anyFeatureRequiresConsent(data),
        guestAppFlow: getGuestAppFlow(data),
        uiVersion: getUiVersion(data),
      };
    },
  });
}
